/**
 * Store Service — datos de contacto de la tienda.
 *
 * Salen del catálogo público de ChekList (campo `contacto`), que los lee de la
 * pestaña Config_General del Google Sheet: claves `tienda_whatsapp` y
 * `tienda_email`. Así se cambian desde la hoja sin volver a publicar la tienda.
 */

import { api, CATALOGO_URL } from './api';
import type { RawCatalogo, RawVendedor } from './api-adapter';

export type Vendedor = RawVendedor;
export type CuponConfig = NonNullable<RawCatalogo['cupon']>;

export interface StoreInfo {
  phone: string;       // raw phone number, e.g. "5350378286"
  whatsapp: string;    // same number formatted for wa.me, e.g. "5350378286"
  phoneDisplay: string; // human-readable, e.g. "+53 XXXX XXXX"
  email: string | null; // contact email — null if not provided
  vendedores: Vendedor[]; // a quién puede escribir el cliente
  cupon: CuponConfig | null; // cupones de vendedor activos
}

/** Si la hoja no tiene teléfono configurado */
const FALLBACK_PHONE = '5350378286';

export function formatDisplay(digits: string): string {
  // Cuban numbers: 53 + 8 digits → "+53 XXXX XXXX"
  if (digits.startsWith('53') && digits.length === 10) {
    return `+53 ${digits.slice(2, 6)} ${digits.slice(6)}`;
  }
  // Generic: +XX XXXXXXXXX
  if (digits.length >= 8) {
    return `+${digits.slice(0, 2)} ${digits.slice(2)}`;
  }
  return `+${digits}`;
}

export function buildInfo(
  phone: string,
  email: string | null,
  vendedores: Vendedor[] = [],
  cupon: CuponConfig | null = null,
): StoreInfo {
  return { phone, whatsapp: phone, phoneDisplay: formatDisplay(phone), email, vendedores, cupon };
}

export async function getStoreInfo(): Promise<StoreInfo> {
  try {
    const raw = await api.get<RawCatalogo>(CATALOGO_URL);
    const phone = (raw.contacto?.whatsapp ?? '').replace(/\D/g, '');
    const email = (raw.contacto?.email ?? '').trim();
    return buildInfo(
      phone.length >= 7 ? phone : FALLBACK_PHONE,
      email.includes('@') ? email : null,
      (raw.vendedores ?? []).filter((v) => v?.codigo && v.whatsapp),
      raw.cupon && raw.cupon.descuentoUsd > 0 ? raw.cupon : null,
    );
  } catch {
    return buildInfo(FALLBACK_PHONE, null);
  }
}
